/**
 * Canonical tracking metrics catalog for Elite Trainr mobile.
 * Source of truth for labels, units, storage keys, Health mappings,
 * and which metrics are enterable via Metric Detail / Quick add.
 *
 * Human-readable twin: project store docs/tracking-metrics-catalog.md
 */

import { userStatTypes } from 'src/app/services/fitness.service';

/** Metrics that participate in daily tracking UX (including display-only). */
export type TrackingMetricId =
  | 'weight'
  | 'sleep'
  | 'cycle'
  | 'steps'
  | 'calories' // active energy burned → userStats.calories
  | 'totalCalories' // total energy expended → userStats.totalCalories
  | 'caloricIntake' // dietary intake → userStats.caloricIntake
  | 'bodyFat'
  | 'cardioMinutes' // session aggregate only — not userStats
  | 'nutritionCalories' // legacy alias → userStats.caloricIntake
  | 'distance'
  | 'flightsClimbed'
  | 'restingCalories'
  | 'restingHeartRate'
  | 'heartRate'
  | 'hrv'
  | 'vo2Max'
  | 'bpSystolic'
  | 'bpDiastolic'
  | 'spo2'
  | 'respiratoryRate'
  | 'height'
  | 'water'
  | 'bloodGlucose'
  | 'bodyTemperature'
  | 'basalBodyTemperature';

export type MetricFieldType = 'number' | 'notes';

export type MetricUnitChoice = {
  value: string;
  labelKey: string;
};

export type TrackingMetricDef = {
  id: TrackingMetricId;
  /** RTDB `userStats/{uid}/{key}` when applicable */
  userStatKey: userStatTypes | null;
  /** i18n key for short UI label */
  labelKey: string;
  /** i18n key for “Track X” / action-sheet title */
  trackLabelKey: string;
  /** i18n key for unit chip beside the input (null = no chip / notes-only) */
  unitLabelKey: string | null;
  /** Default unit string persisted on userStats rows */
  defaultUnit: string | null;
  /** Selectable units (weight). Empty = fixed unit chip */
  unitChoices: MetricUnitChoice[];
  fieldType: MetricFieldType;
  inputMode: 'decimal' | 'numeric' | null;
  showChart: boolean;
  averageSuffix: string;
  /** Opens Metric Detail for enter/edit */
  enterable: boolean;
  /** Appears in footer Quick add picker */
  quickAdd: boolean;
  /**
   * CapGo `@capgo/capacitor-health` dataType written/read by device sync.
   * Null when not synced from Health.
   */
  healthCapgoType: string | null;
  /** Plain-English meaning of the Health type (for docs / mismatch flags) */
  healthMeaning: string | null;
  /** Related metric that must not be confused with this one */
  notConfusedWith: TrackingMetricId | null;
  /** Storage / display notes */
  notes: string;
};

export const TRACKING_METRICS: Record<TrackingMetricId, TrackingMetricDef> = {
  weight: {
    id: 'weight',
    userStatKey: 'weight',
    labelKey: 'tracking.weight',
    trackLabelKey: 'tracking.trackWeight',
    unitLabelKey: null, // uses unitChoices select
    defaultUnit: 'lbs',
    unitChoices: [
      { value: 'lbs', labelKey: 'general.lbs' },
      { value: 'kg', labelKey: 'general.kg' },
    ],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '',
    enterable: true,
    quickAdd: true,
    healthCapgoType: 'weight',
    healthMeaning: 'Body mass (kg from plugin; app stores lbs/kg)',
    notConfusedWith: null,
    notes: 'Health fill-if-empty; manual overwrite allowed.',
  },
  sleep: {
    id: 'sleep',
    userStatKey: 'sleep',
    labelKey: 'tracking.sleep',
    trackLabelKey: 'tracking.trackSleep',
    unitLabelKey: 'general.hours',
    defaultUnit: 'hours',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: 'h',
    enterable: true,
    quickAdd: true,
    healthCapgoType: 'sleep',
    healthMeaning: 'Sleep duration (app stores hours)',
    notConfusedWith: null,
    notes: 'Health fill-if-empty; manual overwrite allowed.',
  },
  cycle: {
    id: 'cycle',
    userStatKey: 'cycle',
    labelKey: 'tracking.cycle',
    trackLabelKey: 'tracking.trackCycle',
    unitLabelKey: null,
    defaultUnit: null,
    unitChoices: [],
    fieldType: 'notes',
    inputMode: null,
    showChart: false,
    averageSuffix: '',
    enterable: true,
    quickAdd: true,
    healthCapgoType: null,
    healthMeaning: null,
    notConfusedWith: null,
    notes: 'Notes + presence flag; no numeric series / no Health sync.',
  },
  steps: {
    id: 'steps',
    userStatKey: 'steps',
    labelKey: 'tracking.steps',
    trackLabelKey: 'tracking.trackSteps',
    unitLabelKey: 'tracking.steps',
    defaultUnit: 'steps',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: '',
    enterable: true,
    quickAdd: true,
    healthCapgoType: 'steps',
    healthMeaning: 'Step count',
    notConfusedWith: null,
    notes:
      'Prefer userStats (Health/cardio sync); fallback cardio session step sessions. Manual overwrite source=manual.',
  },
  calories: {
    id: 'calories',
    userStatKey: 'calories',
    labelKey: 'tracking.caloriesBurned',
    trackLabelKey: 'tracking.trackCaloriesBurned',
    unitLabelKey: 'tracking.kcal',
    defaultUnit: 'kcal',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' kcal',
    enterable: true,
    quickAdd: true,
    healthCapgoType: 'calories',
    healthMeaning:
      'Active energy burned (CapGo). NOT dietaryEnergyConsumed / nutrition intake.',
    notConfusedWith: 'nutritionCalories',
    notes:
      'Apple Health / Health Connect write userStats.calories via CapGo dataType calories = active energy burned. Nutrition calories are separate (nutritionTracking totals).',
  },
  totalCalories: {
    id: 'totalCalories',
    userStatKey: 'totalCalories',
    labelKey: 'tracking.totalCaloriesExpended',
    trackLabelKey: 'tracking.totalCaloriesExpended',
    unitLabelKey: 'tracking.kcal',
    defaultUnit: 'kcal',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' kcal',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'totalCalories',
    healthMeaning:
      'Total energy expended. Android: TotalCaloriesBurned. iOS: activeEnergyBurned + basalEnergyBurned.',
    notConfusedWith: 'calories',
    notes:
      'Synced alongside active calories during Health sync into userStats.totalCalories. Used for Day Summary Active / Total tile.',
  },
  caloricIntake: {
    id: 'caloricIntake',
    userStatKey: 'caloricIntake',
    labelKey: 'tracking.calIn',
    trackLabelKey: 'tracking.calIn',
    unitLabelKey: 'tracking.kcal',
    defaultUnit: 'kcal',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' kcal',
    enterable: true,
    quickAdd: false,
    healthCapgoType: 'dietaryEnergyConsumed',
    healthMeaning: 'Dietary energy consumed (intake)',
    notConfusedWith: 'calories',
    notes:
      'Display only from userStats.caloricIntake. Synced from nutritionTracking totals.calories when trackingCompleted is set; cleared if unmarked/deleted; manual Metric Detail Submit; Health dietaryEnergyConsumed fill-if-empty.',
  },
  bodyFat: {
    id: 'bodyFat',
    userStatKey: 'bodyFat',
    labelKey: 'pages.connectedApps.upsell.metrics.bodyFat',
    trackLabelKey: 'pages.connectedApps.upsell.metrics.bodyFat',
    unitLabelKey: null,
    defaultUnit: '%',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '%',
    enterable: false, // not in Metric Detail v1
    quickAdd: false,
    healthCapgoType: 'bodyFat',
    healthMeaning: 'Body fat percentage',
    notConfusedWith: null,
    notes: 'Synced from Health today; no tracking-page tile / popup yet.',
  },
  cardioMinutes: {
    id: 'cardioMinutes',
    userStatKey: null,
    labelKey: 'tracking.minutes',
    trackLabelKey: 'tracking.minutes',
    unitLabelKey: 'tracking.minutes',
    defaultUnit: 'min',
    unitChoices: [],
    fieldType: 'number',
    inputMode: null,
    showChart: false,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: null,
    healthMeaning: null,
    notConfusedWith: null,
    notes:
      'Display-only sum of cardioTracking session tracking.time. No userStats key.',
  },
  nutritionCalories: {
    id: 'nutritionCalories',
    userStatKey: 'caloricIntake',
    labelKey: 'tracking.calIn',
    trackLabelKey: 'tracking.logFoods',
    unitLabelKey: 'tracking.kcal',
    defaultUnit: 'kcal',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' kcal',
    enterable: true,
    quickAdd: false,
    healthCapgoType: 'dietaryEnergyConsumed',
    healthMeaning: 'Alias of caloricIntake — completed food log syncs into userStats.caloricIntake',
    notConfusedWith: 'calories',
    notes: 'Do not read nutritionTracking for display; sync into userStats.caloricIntake on trackingCompleted.',
  },
  distance: {
    id: 'distance',
    userStatKey: 'distance',
    labelKey: 'tracking.statsWalkingDistance',
    trackLabelKey: 'tracking.statsWalkingDistance',
    unitLabelKey: null,
    defaultUnit: 'mi',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'distance',
    healthMeaning: 'Walking / running distance (meters from plugin)',
    notConfusedWith: null,
    notes: 'Health overwrite daily sum; stored as mi/km by preferred units.',
  },
  flightsClimbed: {
    id: 'flightsClimbed',
    userStatKey: 'flightsClimbed',
    labelKey: 'tracking.statsFlightsClimbed',
    trackLabelKey: 'tracking.statsFlightsClimbed',
    unitLabelKey: null,
    defaultUnit: 'flights',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'flightsClimbed',
    healthMeaning: 'Flights / floors climbed',
    notConfusedWith: null,
    notes: 'Health overwrite daily sum.',
  },
  restingCalories: {
    id: 'restingCalories',
    userStatKey: 'restingCalories',
    labelKey: 'tracking.statsRestingCalories',
    trackLabelKey: 'tracking.statsRestingCalories',
    unitLabelKey: 'tracking.kcal',
    defaultUnit: 'kcal',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' kcal',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'basalCalories',
    healthMeaning: 'Basal / resting energy burned',
    notConfusedWith: 'calories',
    notes: 'Health overwrite daily; also used to derive iOS totalCalories.',
  },
  restingHeartRate: {
    id: 'restingHeartRate',
    userStatKey: 'restingHeartRate',
    labelKey: 'tracking.statsRestingHr',
    trackLabelKey: 'tracking.statsRestingHr',
    unitLabelKey: null,
    defaultUnit: 'bpm',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' bpm',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'restingHeartRate',
    healthMeaning: 'Resting heart rate',
    notConfusedWith: 'heartRate',
    notes: 'Health fill-if-empty latest sample.',
  },
  heartRate: {
    id: 'heartRate',
    userStatKey: 'heartRate',
    labelKey: 'tracking.statsAvgHr',
    trackLabelKey: 'tracking.statsAvgHr',
    unitLabelKey: null,
    defaultUnit: 'bpm',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' bpm',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'heartRate',
    healthMeaning: 'Heart rate samples (stored as daily average)',
    notConfusedWith: 'restingHeartRate',
    notes: 'Health overwrite daily average of samples.',
  },
  hrv: {
    id: 'hrv',
    userStatKey: 'hrv',
    labelKey: 'tracking.statsHrv',
    trackLabelKey: 'tracking.statsHrv',
    unitLabelKey: null,
    defaultUnit: 'ms',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: ' ms',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'heartRateVariability',
    healthMeaning: 'Heart rate variability (ms)',
    notConfusedWith: null,
    notes: 'Health fill-if-empty latest sample.',
  },
  vo2Max: {
    id: 'vo2Max',
    userStatKey: 'vo2Max',
    labelKey: 'tracking.statsVo2Max',
    trackLabelKey: 'tracking.statsVo2Max',
    unitLabelKey: null,
    defaultUnit: 'mL/min/kg',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'vo2Max',
    healthMeaning: 'VO2 max',
    notConfusedWith: null,
    notes: 'Health fill-if-empty latest sample.',
  },
  bpSystolic: {
    id: 'bpSystolic',
    userStatKey: 'bpSystolic',
    labelKey: 'tracking.statsBpSystolic',
    trackLabelKey: 'tracking.statsBpSystolic',
    unitLabelKey: null,
    defaultUnit: 'mmHg',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' mmHg',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'bloodPressure',
    healthMeaning: 'Blood pressure systolic',
    notConfusedWith: 'bpDiastolic',
    notes: 'Written with bpDiastolic from CapGo bloodPressure sample.',
  },
  bpDiastolic: {
    id: 'bpDiastolic',
    userStatKey: 'bpDiastolic',
    labelKey: 'tracking.statsBpDiastolic',
    trackLabelKey: 'tracking.statsBpDiastolic',
    unitLabelKey: null,
    defaultUnit: 'mmHg',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'numeric',
    showChart: true,
    averageSuffix: ' mmHg',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'bloodPressure',
    healthMeaning: 'Blood pressure diastolic',
    notConfusedWith: 'bpSystolic',
    notes: 'Written with bpSystolic from CapGo bloodPressure sample.',
  },
  spo2: {
    id: 'spo2',
    userStatKey: 'spo2',
    labelKey: 'tracking.statsSpo2',
    trackLabelKey: 'tracking.statsSpo2',
    unitLabelKey: null,
    defaultUnit: '%',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '%',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'oxygenSaturation',
    healthMeaning: 'Blood oxygen saturation',
    notConfusedWith: null,
    notes: 'Health fill-if-empty latest sample.',
  },
  respiratoryRate: {
    id: 'respiratoryRate',
    userStatKey: 'respiratoryRate',
    labelKey: 'tracking.statsRespiratoryRate',
    trackLabelKey: 'tracking.statsRespiratoryRate',
    unitLabelKey: null,
    defaultUnit: '/min',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: ' /min',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'respiratoryRate',
    healthMeaning: 'Respiratory rate',
    notConfusedWith: null,
    notes: 'Health fill-if-empty latest sample.',
  },
  height: {
    id: 'height',
    userStatKey: 'height',
    labelKey: 'tracking.statsHeight',
    trackLabelKey: 'tracking.statsHeight',
    unitLabelKey: null,
    defaultUnit: 'in',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'height',
    healthMeaning: 'Body height (cm from plugin)',
    notConfusedWith: null,
    notes: 'Health fill-if-empty; stored as in/cm by preferred units.',
  },
  water: {
    id: 'water',
    userStatKey: 'water',
    labelKey: 'tracking.statsWater',
    trackLabelKey: 'tracking.statsWater',
    unitLabelKey: null,
    defaultUnit: 'oz',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'dietaryWater',
    healthMeaning: 'Water consumed (liters from plugin)',
    notConfusedWith: null,
    notes: 'Health overwrite daily sum; stored as oz/L by preferred units.',
  },
  bloodGlucose: {
    id: 'bloodGlucose',
    userStatKey: 'bloodGlucose',
    labelKey: 'tracking.statsBloodGlucose',
    trackLabelKey: 'tracking.statsBloodGlucose',
    unitLabelKey: null,
    defaultUnit: 'mg/dL',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: ' mg/dL',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'bloodGlucose',
    healthMeaning: 'Blood glucose',
    notConfusedWith: null,
    notes: 'Health fill-if-empty latest sample.',
  },
  bodyTemperature: {
    id: 'bodyTemperature',
    userStatKey: 'bodyTemperature',
    labelKey: 'tracking.statsBodyTemp',
    trackLabelKey: 'tracking.statsBodyTemp',
    unitLabelKey: null,
    defaultUnit: '°F',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '°',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'bodyTemperature',
    healthMeaning: 'Body temperature (celsius from plugin)',
    notConfusedWith: 'basalBodyTemperature',
    notes: 'Health fill-if-empty; stored °F/°C by preferred units.',
  },
  basalBodyTemperature: {
    id: 'basalBodyTemperature',
    userStatKey: 'basalBodyTemperature',
    labelKey: 'tracking.statsBasalBodyTemp',
    trackLabelKey: 'tracking.statsBasalBodyTemp',
    unitLabelKey: null,
    defaultUnit: '°F',
    unitChoices: [],
    fieldType: 'number',
    inputMode: 'decimal',
    showChart: true,
    averageSuffix: '°',
    enterable: false,
    quickAdd: false,
    healthCapgoType: 'basalBodyTemperature',
    healthMeaning: 'Basal body temperature (celsius from plugin)',
    notConfusedWith: 'bodyTemperature',
    notes: 'Health fill-if-empty; stored °F/°C by preferred units.',
  },
};

/** Footer Quick add order */
export const QUICK_ADD_METRIC_IDS: TrackingMetricId[] = [
  'weight',
  'sleep',
  'steps',
  'calories',
  'cycle',
];

export function getTrackingMetric(id: TrackingMetricId): TrackingMetricDef {
  return TRACKING_METRICS[id];
}

/** Resolve catalog entry from a userStats key (null if not mapped). */
export function getMetricByUserStatKey(
  key: userStatTypes,
): TrackingMetricDef | null {
  const match = Object.values(TRACKING_METRICS).find((m) => m.userStatKey === key);
  return match || null;
}

export function isEnterableUserStat(key: userStatTypes): boolean {
  const def = getMetricByUserStatKey(key);
  return !!def?.enterable && !!def.userStatKey;
}

/**
 * Known `userStats.*.source` values written by Health sync, cardio triggers,
 * and Metric Detail saves — mapped to i18n label keys.
 */
export const USER_STAT_SOURCE_LABEL_KEYS: Record<string, string> = {
  appleHealth: 'tracking.metricSourceAppleHealth',
  healthConnect: 'tracking.metricSourceHealthConnect',
  cardio: 'tracking.metricSourceCardio',
  nutrition: 'tracking.metricSourceFoodLog',
  checkin: 'tracking.metricSourceEliteTrainr',
  manual: 'tracking.metricSourceEliteTrainr',
};

/**
 * Resolve display label key for a stored source.
 * When a value exists but `source` is missing (legacy rows), treat as Elite Trainr.
 */
export function getUserStatSourceLabelKey(
  source: string | null | undefined,
  opts?: { valuePresent?: boolean },
): string | null {
  if (source && USER_STAT_SOURCE_LABEL_KEYS[source]) {
    return USER_STAT_SOURCE_LABEL_KEYS[source];
  }
  if (source) {
    return null; // unknown code — caller may show raw
  }
  if (opts?.valuePresent) {
    return 'tracking.metricSourceEliteTrainr';
  }
  return null;
}
